import React, { useMemo } from 'react';
import useAsyncEffect from 'lib/frontend/hooks/useAsyncEffect';
import { ColumnService } from 'lib/services/directory';
import { OrganizationType } from 'lib/enums';
import { Permissions } from 'lib/permissions/roles';
import { getFirebaseContext } from 'utils/firebase';
import { getOrThrow } from 'lib/utils/refs';
import { mapAsync } from 'lib/helpers/array/async';
import { ColumnSelectOption } from 'lib/components/ColumnSelect';
import { selectAvailableOrganizations, selectUser } from 'redux/auth';
import { useAppSelector } from 'redux/hooks';
import { TabHeader } from './TabHeader';
import { DateRangePickerWithModal } from './DateRangePickerWithModal';
import { OrganizationSelector } from './OrganizationSelector';
import {
  orgHasRelevantStatusForReport,
  useReportParams
} from '../context/ReportParamsContext';
import { useReportsLoading } from '../context/LoadingContext';

type ReportHeaderProps = {
  title: string;
  subtitle?: string;
  subtitleToolTip?: string;
  permission?: Permissions;
  hideDateRangePicker?: boolean;
};

const getRelevantOrgs = async (
  orgIds: string[]
): Promise<ColumnSelectOption<string>[]> => {
  const [relevantOrgsErr, relevantOrgs] = await mapAsync(
    orgIds,
    async orgId =>
      await getOrThrow(getFirebaseContext().organizationsRef().doc(orgId))
  );
  if (relevantOrgsErr) {
    return [];
  }
  return relevantOrgs
    .filter(org => {
      return (
        org.data().organizationType === OrganizationType.newspaper.value &&
        orgHasRelevantStatusForReport(org)
      );
    })
    .map(org => ({
      value: org.id,
      label: org.data()?.name ?? ''
    }))
    .sort((a, b) => a.label.localeCompare(b.label));
};

export function ReportHeader({
  title,
  subtitle,
  subtitleToolTip,
  permission,
  hideDateRangePicker = false
}: ReportHeaderProps) {
  const {
    getDateRange,
    getOrgIds,
    getAvailableOrgIdsWithPermission,
    updateOrgIds
  } = useReportParams();
  const { withLoading } = useReportsLoading();

  const availableOrganizations = useAppSelector(selectAvailableOrganizations);

  const { dateRange } = getDateRange();
  const user = useAppSelector(selectUser);

  // Use permission-aware organization IDs if permission is provided
  const currentOrgIds = permission
    ? getAvailableOrgIdsWithPermission(permission)
    : availableOrganizations?.map(org => org.id) ?? [];

  const { value: fetchedOrgs } = useAsyncEffect({
    fetchData: async () => await withLoading(getRelevantOrgs(currentOrgIds)),
    dependencies: [user?.id],
    errorConfig: {
      service: ColumnService.AUTH_AND_USER_MANAGEMENT,
      message: 'Failed to fetch allowed organizations'
    }
  });

  const values = useMemo(() => {
    // Filter out orgIds that are in the URL but may not be in the fetchedOrgs, so that it does not populate the value of the report header org selector.
    // This can happen if the user is given a shared URL with orgIds that it does not have access to as an available organization or a permissioned organization.
    const fetchedOrgsMap = new Set(fetchedOrgs?.map(org => org.value) ?? []);
    return (
      getOrgIds().filter(orgId => {
        return fetchedOrgsMap.has(orgId);
      }) || []
    );
  }, [getOrgIds, fetchedOrgs]);

  return (
    <TabHeader
      title={title}
      subheading={subtitle}
      subheadingToolTip={subtitleToolTip}
    >
      {!hideDateRangePicker && <DateRangePickerWithModal value={dateRange} />}
      {availableOrganizations && availableOrganizations.length > 1 && (
        <OrganizationSelector
          value={values || []}
          options={fetchedOrgs}
          onChange={updateOrgIds}
        />
      )}
    </TabHeader>
  );
}
